<template>
  <div class="visits-overview-comment">
    <div class="visits-overview-comment__header">
      <div class="visits-overview-comment__author">
        {{ comment.WhoAdded }}
      </div>
      <div class="visits-overview-comment__date">
        {{ moment(comment.DateAdded).format("MM/DD/YYYY h:mm a") }}
      </div>
    </div>

    <div class="visits-overview-comment__body">
      {{ commentText }}
      <span
        v-if="needExpand"
        class="expand-link"
        @click="toggleExpand"
        data-cy="expand-comment"
      >
        {{ expanded ? "Show less" : "Show more" }}
      </span>
    </div>
  </div>
</template>

<script setup lang="ts">
import type { VisitInspectionCommentDisplay } from "@/Domain/VisitInspectionCommentDisplay";
import moment from "moment";
import { computed, ref } from "vue";

const props = defineProps<{
  comment: VisitInspectionCommentDisplay;
}>();

const expanded = ref(false);

const expandLimit = 150; // Limit for the comment to show before expanding
const expandLineLimit = 3; // Limit for the number of lines to show before expanding
const expandThreshold = 170; // Character limit for the Expand to be shown

const lineBreaks = computed(() => {
  return props.comment.Comment.split("\n").length;
});

const needExpand = computed(() => {
  return (
    props.comment.Comment.length > expandThreshold ||
    lineBreaks.value > expandLineLimit
  );
});

const commentText = computed(() => {
  if (!needExpand.value || expanded.value) {
    return props.comment.Comment;
  } else {
    const lines = props.comment.Comment.split("\n");
    let value = props.comment.Comment;
    if (lines.length > expandLineLimit)
      value = lines.slice(0, expandLineLimit).join("\n") + "...";
    if (value.length > expandLimit)
      return value.substring(0, expandLimit) + "...";
    return value;
  }
});

const toggleExpand = () => {
  expanded.value = !expanded.value;
};
</script>

<style lang="scss">
.visits-overview-comment {
  border: 1px solid #e0e0e0;
  border-radius: 6px;
  padding: 12px;
  margin-bottom: 15px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
  background-color: #ffffff;

  &__header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 8px;
    padding-bottom: 8px;
    border-bottom: 1px solid #f0f0f0;
  }

  &__author {
    font-weight: 600;
    color: #333333;
    font-size: 0.9rem;
  }

  &__date {
    color: #666666;
    font-size: 0.8rem;
  }

  &__body {
    font-size: 0.95rem;
    line-height: 1.5;
    color: #444444;
    white-space: pre-wrap;
  }

  .expand-link {
    color: #007bff;
    cursor: pointer;
    font-size: 0.9rem;
    margin-top: 8px;
    display: inline-block;
    text-decoration: underline;

    &:hover {
      text-decoration: none;
    }
  }
}
</style>
